/**
 * Textos e escolhas da ficha "Evolução auditiva" (#461). Nenhuma regra de critério mora aqui:
 * situação, piora e médias chegam prontas do servidor, e este arquivo só as escreve em português.
 */
import {
  AUDIOMETRY_FREQUENCIES,
  type AudiometryEar,
  type AudiometryHistoryExam,
} from '../../../types/audiometry';
import { ROTULO_DA_ORELHA, formatarDia, formatarHz } from '../../../utils/audiometria';

export function primeiroNome(nome: string | undefined): string | null {
  const primeiro = nome?.trim().split(/\s+/)[0];
  return primeiro ? primeiro : null;
}

export function formatarMedias(par: { right: number | null; left: number | null }): string {
  if (par.right === null && par.left === null) return '—';
  return `${par.right ?? '—'} / ${par.left ?? '—'} dB`;
}

export interface SituacaoDoExame {
  rotulo: string;
  cor: 'error' | 'success' | 'warning' | 'default' | 'info';
}

export function temLimiares(exame: AudiometryHistoryExam): boolean {
  return exame.thresholds !== null;
}

export function situacaoDoExame(exame: AudiometryHistoryExam): SituacaoDoExame {
  if (!temLimiares(exame)) return { rotulo: 'Sem limiares', cor: 'default' };
  if (exame.comparison) {
    switch (exame.comparison.status) {
      case 'WORSENED':
        return { rotulo: '▲ Agravamento', cor: 'error' };
      case 'STABLE':
        return { rotulo: 'Estável', cor: 'success' };
      case 'NOT_COMPARABLE':
        return { rotulo: 'Sem comparação', cor: 'warning' };
      case 'BEFORE_REFERENCE':
        return { rotulo: 'Anterior à referência', cor: 'default' };
    }
  }
  if (exame.role === 'REFERENCE') return { rotulo: 'Ponto de comparação', cor: 'info' };
  if (exame.role === 'NO_REFERENCE') return { rotulo: 'Sem referência', cor: 'warning' };
  return { rotulo: 'Sem comparação', cor: 'warning' };
}

export const ROTULO_DO_PAPEL: Record<AudiometryHistoryExam['role'], string> = {
  REFERENCE: 'Referência',
  SEQUENTIAL: 'Sequencial',
  BEFORE_REFERENCE: 'Anterior à referência',
  NO_REFERENCE: 'Sem referência',
};

export interface PontoDoAudiograma {
  frequencia: string;
  referencia: number | null;
  ultimo: number | null;
}

/** Uma linha por frequência, com o limiar da referência e o do último exame naquela orelha. */
export function pontosDaOrelha(
  ear: AudiometryEar,
  referencia: AudiometryHistoryExam | null,
  ultimo: AudiometryHistoryExam,
): PontoDoAudiograma[] {
  return AUDIOMETRY_FREQUENCIES.map((frequencia) => {
    const chave = `${frequencia}` as const;
    return {
      frequencia: frequencia >= 1000 ? `${frequencia / 1000}k` : `${frequencia}`,
      referencia: referencia?.thresholds?.[ear][chave] ?? null,
      ultimo: ultimo.thresholds?.[ear][chave] ?? null,
    };
  });
}

/** Texto do `aria-label` do gráfico: quem não enxerga o desenho ouve os mesmos números. */
export function descreverAudiograma(
  ear: AudiometryEar,
  pontos: PontoDoAudiograma[],
  ultimo: AudiometryHistoryExam,
  referencia: AudiometryHistoryExam | null,
): string {
  const serie = (chave: 'referencia' | 'ultimo') =>
    pontos
      .map((p, i) => {
        const valor = p[chave];
        return valor === null ? null : `${formatarHz(AUDIOMETRY_FREQUENCIES[i]!)} Hz ${valor} dB`;
      })
      .filter((texto): texto is string => texto !== null)
      .join(', ');

  const partes = [
    `Audiograma da ${ROTULO_DA_ORELHA[ear].toLowerCase()}. Quanto mais para baixo, pior a audição.`,
    `Último exame, ${formatarDia(ultimo.date)}: ${serie('ultimo') || 'sem valores'}.`,
  ];
  if (referencia) {
    partes.push(`Referência, ${formatarDia(referencia.date)}: ${serie('referencia') || 'sem valores'}.`);
  }
  return partes.join(' ');
}
